<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';
import summaryReportsAPI from 'dashboard/api/summaryReports';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await summaryReportsAPI.getMySummary(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const ROWS = [
  { key: 'conversations_count', labelKey: 'CONVERSATIONS_COUNT' },
  { key: 'resolved_conversations_count', labelKey: 'RESOLVED_COUNT' },
  {
    key: 'avg_first_response_time',
    labelKey: 'AVG_FIRST_RESPONSE_TIME',
    hintKey: 'REPORT.METRICS.FIRST_RESPONSE_TIME.HINT',
    format: formatTime,
  },
  {
    key: 'avg_resolution_time',
    labelKey: 'AVG_RESOLUTION_TIME',
    hintKey: 'REPORT.METRICS.RESOLUTION_TIME.HINT',
    format: formatTime,
  },
  { key: 'avg_reply_time', labelKey: 'AVG_REPLY_TIME', format: formatTime },
];

const displayValue = row => {
  const raw = data.value?.[row.key];
  if (raw === undefined || raw === null) return '—';
  return row.format ? row.format(raw) : raw;
};
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.MEU_ESPACO.SECTIONS.SERVICE') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.MEU_ESPACO.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.MEU_ESPACO.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.MEU_ESPACO.LOADING') }}
    </p>
    <div v-else class="px-3 pb-3 overflow-y-auto overflow-x-auto max-h-80">
      <table class="w-full text-sm">
        <tbody>
          <tr v-for="row in ROWS" :key="row.key" class="border-t border-n-weak">
            <td
              v-tooltip="row.hintKey ? t(row.hintKey) : undefined"
              class="py-1 pr-3 text-n-slate-10"
            >
              {{ t(`DASHBOARD.MEU_ESPACO.SERVICE.${row.labelKey}`) }}
            </td>
            <td class="py-1 pr-3 font-medium text-n-slate-12">
              {{ displayValue(row) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
